package com.eladfinish.bible.contest.ui.components.dialogs


import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.ShapeDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.cancel
import com.eladfinish.bible.contest.menu_refresh
import com.eladfinish.bible.contest.ui.common.RtlContainer
import com.eladfinish.bible.contest.ui.extensions.modifyIfNotNull
import com.eladfinish.bible.contest.ui.icons.AppIcons
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.desc.StringDesc
import dev.icerock.moko.resources.desc.desc

@Composable
fun BasicDialogWithTitle(
    onDismiss: () -> Unit,
    title: String,
    withArrows: Boolean = false,
    onPreviousClick: () -> Unit = {},
    onNextClick: () -> Unit = {},
    onTitleClick: (() -> Unit)? = null,
    fontFamily: FontFamily? = null,
    onCancel: (() -> Unit)? = null,
    cancelText: StringDesc = SharedRes.strings.cancel.desc(),
    onAction: (() -> Unit)? = null,
    actionText: StringDesc? = null,
    content: @Composable ColumnScope.() -> Unit,
) {
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(
            dismissOnBackPress = true,
            dismissOnClickOutside = true,
            usePlatformDefaultWidth = true,
        ),
    ) {
        OutlinedCard(
            modifier = Modifier.heightIn(max = 600.dp),
            shape = ShapeDefaults.Medium,
            colors = CardDefaults.elevatedCardColors(),
            elevation = CardDefaults.elevatedCardElevation(),
        ) {
            val titleModifier = Modifier.modifyIfNotNull(data = onTitleClick) {
                clickable { it() }
            }

            if (withArrows) {
                DialogHeaderWithArrows(
                    text = title,
                    onPreviousClick = onPreviousClick,
                    onNextClick = onNextClick,
                    modifier = titleModifier,
                )
            } else {
                CustomTitle(
                    text = title,
                    modifier = titleModifier,
                    fontFamily = fontFamily,
                )
            }

            // Content sizes to fit (no forced minimum height, so short content never leaves a
            // dead gap above the footer); `fill = false` lets it shrink below the leftover space
            // when it doesn't need it, while still scrolling within that space when it's longer.
            Column(modifier = Modifier.weight(1f, fill = false)) {
                content()
            }

            if (onCancel != null) {
                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    tonalElevation = 6.dp, // shadow-like elevation in M3
                    shadowElevation = 8.dp,
                ) {
                    // Surface's content isn't a ColumnScope, so the divider and the button row
                    // must be wrapped in an explicit Column -- otherwise they'd stack on top of
                    // each other instead of one appearing above the other.
                    Column(modifier = Modifier.fillMaxWidth()) {
                        HorizontalDivider()
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(16.dp),
                            horizontalArrangement = Arrangement.End,
                        ) {
                            if (onAction != null && actionText != null) {
                                // Same border as the cancel OutlinedButton uses by default,
                                // so the two footer buttons look consistent.
                                TextButton(
                                    onClick = onAction,
                                    border = ButtonDefaults.outlinedButtonBorder(enabled = true),
                                ) {
                                    Text(actionText.localized())
                                }
                                Spacer(modifier = Modifier.width(8.dp))
                            }
                            OutlinedButton(onClick = onCancel) {
                                Text(cancelText.localized())
                            }
                        }
                    }
                }
            }
        }
    }
}


@Composable
private fun DialogHeaderWithArrows(
    text: String,
    onPreviousClick: () -> Unit,
    onNextClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    RtlContainer {
        Row(
            modifier = modifier
                .fillMaxWidth()
                .padding(vertical = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            IconButton(onClick = onPreviousClick) {
                Icon(
                    imageVector = AppIcons.ChevronRight,
                    contentDescription = "Next",
                )
            }

            CustomTitle(
                text = text,
                modifier = Modifier.weight(1f),
                fontSize = 24.sp,
            )

            IconButton(onClick = onNextClick) {
                Icon(
                    imageVector = AppIcons.ChevronLeft,
                    contentDescription = "Previous",
                )
            }
        }
    }
}

@Preview
@Composable
fun BasicDialogWithTitleWithArrowsPreview() {
    BasicDialogWithTitle(
        onDismiss = {},
        title = "בראשית פרק א'",
        withArrows = true,
        onCancel = {}, // shows cancel button
        content = {},
    )
}

@Preview
@Composable
fun BasicDialogWithTitleWithActionPreview() {
    BasicDialogWithTitle(
        onDismiss = {},
        title = "חידון התנ\"ך לנוער",
        onCancel = {},
        onAction = {}, // shows the optional extra action button next to cancel
        actionText = SharedRes.strings.menu_refresh.desc(),
        content = {},
    )
}
